/**
 * "Sent" means the server said so.
 *
 * A frame written to a WebSocket is not a delivered frame: the socket can be
 * half-dead for seconds before the browser notices — behind a relay on a
 * flapping VPN it was — and everything written in that window vanishes
 * without an error. The send job used to finish the moment it had written
 * its frames, so the message was gone from the queue and from the server at
 * once, and the bubble kept its little clock for ever (Pixel, 2026-09-02).
 *
 * This is the other half: the job registers the acknowledgements it expects
 * (one per copy — the server answers every chat_message with a chat_sent,
 * or with chat_sent{duplicate:true} for a repeat), writes the frames, and
 * finishes only when they all came back. A missing acknowledgement is a
 * timeout, and a timeout is a retry: the server collapses a repeat onto the
 * row it already has by (client_mid, target_device_id), so a resend can
 * never duplicate a message. Signal's rule, stated once: a send job is done
 * when the server's response says it is, not when the bytes left.
 */
const waiters = new Map();

export const ACK_TIMEOUT_MS = 15_000;

export const ackKey = (clientMid, targetDeviceId) => `${clientMid}|${targetDeviceId || ''}`;

/** A promise that resolves when `ack` is called with this key, or rejects after `timeoutMs`. */
// The host's timers, called ON the host: a browser's setTimeout refuses to
// run as a method of any other object («Illegal invocation»), and Node's
// does not mind — which is how a green test suite shipped a send that
// failed on every phone (2026-09-02).
const hostTimers = {
  setTimeout: (fn, ms) => globalThis.setTimeout(fn, ms),
  clearTimeout: (h) => globalThis.clearTimeout(h),
};

export function expectAck(key, timeoutMs = ACK_TIMEOUT_MS, timers = hostTimers) {
  return new Promise((resolve, reject) => {
    const prev = waiters.get(key);
    if (prev) { timers.clearTimeout(prev.timer); prev.reject(new Error('superseded')); }
    const timer = timers.setTimeout(() => {
      waiters.delete(key);
      reject(new Error(`нет подтверждения от сервера за ${Math.round(timeoutMs / 1000)} с`));
    }, timeoutMs);
    waiters.set(key, { resolve, reject, timer, timers });
  });
}

/** The server acknowledged this copy. Returns whether anyone was waiting. */
export function ack(key) {
  const w = waiters.get(key);
  if (!w) return false;
  waiters.delete(key); w.timers.clearTimeout(w.timer); w.resolve(true);
  return true;
}

export function pendingAcks() { return waiters.size; }
